import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { useBreadcrumbs } from "@/contexts/BreadcrumbContext";
import { logger } from "@/utils/logger";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  BarChart3,
  Cpu,
  FileWarning,
  Gauge,
  ListChecks,
  Info,
  ArrowRight,
  Activity,
  Loader2,
  PieChart as PieChartIcon,
  TrendingUp,
  Tags,
  MenuSquare,
} from "lucide-react";
import {
  Label as RechartsLabel,
  PolarGrid,
  PolarRadiusAxis,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  BarChart,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
} from "recharts";

// --- Interfaces (Matching api.py StatisticsSchema) ---

// Corresponds to RunSummarySchema in api.py
interface RunSummarySchema {
  id: string;
  run_id: string;
  timestamp_start: string;
  timestamp_end?: string | null;
  total_queries_attempted: number;
  total_queries_succeeded: number;
  total_queries_failed: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_tokens_processed: number;
  total_estimated_cost: number;
  total_actual_cost: number;
  config_details?: object | null;
  total_duration_ms?: number | null;
}

// Corresponds to SuccessRateSchema in api.py
interface SuccessRateSchema {
  succeeded: number;
  failed: number;
  attempted: number;
}

// Corresponds to ScoreDistributionBucketSchema in api.py
interface ScoreDistributionBucketSchema {
  range_start: number;
  count: number;
}

// Corresponds to CategoryUpdateCountSchema in api.py
interface CategoryUpdateCountSchema {
  category: string;
  count: number;
}

// Corresponds to SectionTitleUpdateCountSchema in api.py
interface SectionTitleUpdateCountSchema {
  section_title: string;
  count: number;
}

// Corresponds to TokenUsageByNoteSchema in api.py
interface TokenUsageByNoteSchema {
  note_id: string;
  note_title?: string | null;
  total_tokens: number;
}

// Corresponds to StatisticsSchema in api.py
interface StatisticsSchema {
  run_history: RunSummarySchema[];
  latest_run_success_rate?: SuccessRateSchema | null;
  relevance_score_distribution: ScoreDistributionBucketSchema[];
  similarity_score_distribution: ScoreDistributionBucketSchema[];
  updates_needed_by_category: CategoryUpdateCountSchema[];
  updates_needed_by_section_title: SectionTitleUpdateCountSchema[];
  token_usage_by_note: TokenUsageByNoteSchema[];
  current_missing_articles_count: number;
  article_status_distribution: Array<{ status: string; count: number }>;
  article_assignment_distribution: Array<{
    assignee: {
      username: string;
      name?: string | null;
      avatar_url?: string | null;
    };
    count: number;
  }>;
  release_note_status_distribution: Array<{ status: string; count: number }>;
  release_note_assignment_distribution: Array<{
    assignee: {
      username: string;
      name?: string | null;
      avatar_url?: string | null;
    };
    count: number;
  }>;
}

// Interface for data processed for Bar Charts (e.g. score distributions)
// interface BarChartDataPoint {
//   name: string;
//   value: number;
// }

// Interface for data processed for Pie Charts
interface PieChartDataPoint {
  name: string;
  value: number;
}

// --- Add interfaces needed for charts (from existing code, review if still needed) ---
interface ReleaseNoteSummarySchema {
  _id: string;
  title: string;
  version?: string | null;
  item_count: number;
  update_needed_count: number;
}

// interface PaginatedNotesSchema {
//   total_notes: number;
//   notes: ReleaseNoteSummarySchema[];
//   page: number;
//   page_size: number;
//   total_pages: number;
// }

interface PipelineTriggerOptions {
  run_fetch?: boolean;
  run_preprocess?: boolean;
  run_analyze?: boolean;
  run_prepare_queries?: boolean;
  run_execute_queries?: boolean;
  run_populate_db?: boolean;
  run_generate_missing?: boolean;
  run_all?: boolean;
  purge_db?: boolean;
  max_queries?: number | null;
  model_id?: string | null;
}

interface TriggerPipelineResponse {
  message: string;
  triggered_at: string;
  arguments: string[];
}

interface RerunFailedQueriesResponse {
  message: string;
  triggered_at: string;
  arguments: string[];
}

interface StatisticsProps {
  apiBaseUrl: string;
}

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#AF19FF",
  "#FF19AF",
  "#19AFFF",
  "#FFAF19",
];

// --- Trigger Pipeline Function (existing) ---
const triggerPipeline = async (
  apiBaseUrl: string,
  options: PipelineTriggerOptions,
): Promise<TriggerPipelineResponse | null> => {
  try {
    const validOptions = Object.entries(options)
      .filter(([, value]) => value !== undefined && value !== null)
      .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});
    logger.info(`Triggering pipeline run with options:`, validOptions);
    const url = `${apiBaseUrl}/pipeline/run`;
    const response = await axios.post<TriggerPipelineResponse>(
      url,
      validOptions,
    );
    if (response.status === 202) {
      logger.info("Pipeline run successfully initiated.", {
        responseData: response.data,
      });
      return response.data;
    } else {
      logger.warn(
        `Pipeline trigger returned unexpected status: ${response.status}`,
      );
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    logger.error("Error triggering pipeline run", {
      error: error instanceof Error ? error.message : String(error),
      axiosError: axios.isAxiosError(error) ? error.response?.data : undefined,
    });
    alert(
      `Failed to trigger pipeline: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
    );
    return null;
  }
};

const rerunFailedQueries = async (
  apiBaseUrl: string,
  run_id: string,
): Promise<RerunFailedQueriesResponse | null> => {
  try {
    logger.info(`Retrying failed queries for run: ${run_id}`);
    const url = `${apiBaseUrl}/pipeline/retry-failed/${run_id}`;
    const response = await axios.post<RerunFailedQueriesResponse>(url);

    if (response.status === 202) {
      logger.info("Failed queries retry successfully initiated.", {
        responseData: response.data,
      });
      return response.data;
    } else {
      logger.warn(
        `Retry failed queries returned unexpected status: ${response.status}`,
      );
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    logger.error("Error retrying failed queries", {
      error: error instanceof Error ? error.message : String(error),
      axiosError: axios.isAxiosError(error) ? error.response?.data : undefined,
    });
    return null;
  }
};

// --- Chart Components ---

interface ScoreDistributionChartProps {
  data: ScoreDistributionBucketSchema[];
  title: string;
  barColor: string;
}

const ScoreDistributionChart: React.FC<ScoreDistributionChartProps> = ({
  data,
  title,
  barColor,
}) => {
  const chartData = useMemo(() => {
    return data
      .map((bucket) => ({
        name: bucket.range_start.toFixed(1),
        count: bucket.count,
      }))
      .sort((a, b) => parseFloat(a.name) - parseFloat(b.name));
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-base-content/70">
        No {title.toLowerCase()} data available.
      </p>
    );
  }

  return (
    <div className="card flex h-full flex-col bg-base-100 shadow">
      <div className="card-body p-4">
        <h3 className="card-title flex items-center gap-2 text-lg">
          <TrendingUp className="h-5 w-5 text-primary/80" /> {title}
        </h3>
        <p className="mb-4 text-sm text-base-content/70">
          Distribution of scores.
        </p>
        <div className="flex-grow" style={{ minHeight: "250px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 5, right: 20, left: -20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip
                wrapperClassName="tooltip-wrapper"
                contentStyle={{
                  backgroundColor: "hsl(var(--b1))",
                  borderRadius: "0.5rem",
                }}
              />
              <Bar dataKey="count" fill={barColor} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

interface UpdatesByCategoryChartProps {
  data: CategoryUpdateCountSchema[];
}

const UpdatesByCategoryChart: React.FC<UpdatesByCategoryChartProps> = ({
  data,
}) => {
  const chartData: PieChartDataPoint[] = useMemo(() => {
    return data
      .map((item) => ({ name: item.category, value: item.count }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-base-content/70">
        No category update data available.
      </p>
    );
  }

  return (
    <div className="card flex h-full flex-col bg-base-100 shadow">
      <div className="card-body p-4">
        <h3 className="card-title flex items-center gap-2 text-lg">
          <Tags className="h-5 w-5 text-primary/80" /> Updates by Category
        </h3>
        <p className="mb-4 text-sm text-base-content/70">
          Articles needing updates, by category.
        </p>
        <div className="flex-grow" style={{ minHeight: "250px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) =>
                  `${name} (${(percent * 100).toFixed(0)}%)`
                }
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                stroke="hsl(var(--b1))"
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${entry.name}-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                wrapperClassName="tooltip-wrapper"
                contentStyle={{
                  backgroundColor: "hsl(var(--b1))",
                  borderRadius: "0.5rem",
                }}
              />
              <Legend wrapperStyle={{ fontSize: "12px" }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

interface UpdatesBySectionChartProps {
  data: SectionTitleUpdateCountSchema[];
}

const UpdatesBySectionChart: React.FC<UpdatesBySectionChartProps> = ({
  data,
}) => {
  const chartData = useMemo(() => {
    return data
      .map((item) => ({ name: item.section_title, value: item.count }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-base-content/70">
        No section update data available.
      </p>
    );
  }

  return (
    <div className="card flex h-full flex-col bg-base-100 shadow">
      <div className="card-body p-4">
        <h3 className="card-title flex items-center gap-2 text-lg">
          <MenuSquare className="h-5 w-5 text-primary/80" /> Updates by Section
          Title
        </h3>
        <p className="mb-4 text-sm text-base-content/70">
          Top sections requiring content updates.
        </p>
        <div className="flex-grow" style={{ minHeight: "300px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis
                type="number"
                allowDecimals={false}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                dataKey="name"
                type="category"
                tick={{ fontSize: 10 }}
                width={150}
                interval={0}
              />
              <Tooltip
                wrapperClassName="tooltip-wrapper"
                contentStyle={{
                  backgroundColor: "hsl(var(--b1))",
                  borderRadius: "0.5rem",
                }}
              />
              <Legend wrapperStyle={{ fontSize: "12px" }} />
              <Bar
                dataKey="value"
                name="Updates Needed"
                fill="#82ca9d"
                radius={[0, 4, 4, 0]}
                barSize={15}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

interface TokenUsageByNoteChartProps {
  data: TokenUsageByNoteSchema[];
  notes: ReleaseNoteSummarySchema[];
}

const TokenUsageByNoteChart: React.FC<TokenUsageByNoteChartProps> = ({
  data,
  notes,
}) => {
  const tokenChartData = useMemo(() => {
    if (!data) return [];

    const notesMap = new Map<
      string,
      { title?: string | null; version?: string | null }
    >();
    if (notes) {
      notes.forEach((note) => {
        notesMap.set(note._id, { title: note.title, version: note.version });
      });
    }

    const combinedData = data
      .map((item) => {
        const noteInfo = notesMap.get(item.note_id);
        const name = noteInfo?.version
          ? `${noteInfo.version} - ${noteInfo.title}`
          : item.note_title || item.note_id;
        return {
          name: name.length > 30 ? name.substring(0, 27) + "..." : name,
          value: item.total_tokens,
        };
      })
      .sort((a, b) => b.value - a.value)
      .slice(0, 15);

    return combinedData;
  }, [data, notes]);

  if (!data || data.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-base-content/70">
        No token usage data available.
      </p>
    );
  }

  return (
    <div className="card flex h-full flex-col bg-base-100 shadow">
      <div className="card-body p-4">
        <h3 className="card-title flex items-center gap-2 text-lg">
          <Cpu className="h-5 w-5 text-primary/80" /> Token Usage by Note
        </h3>
        <p className="mb-4 text-sm text-base-content/70">
          Token consumption per release note during analysis.
        </p>
        <div className="flex-grow" style={{ minHeight: "300px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={tokenChartData}
              margin={{ top: 5, right: 20, left: -20, bottom: 50 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 10 }}
                angle={-45}
                textAnchor="end"
                interval={0}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                wrapperClassName="tooltip-wrapper"
                contentStyle={{
                  backgroundColor: "hsl(var(--b1))",
                  borderRadius: "0.5rem",
                }}
              />
              <Bar
                dataKey="value"
                name="Total Tokens"
                fill="#ffc658"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// RunDurationChart (existing, slightly adapted for consistency)
const RunDurationChart = ({
  runSummary,
}: {
  runSummary: RunSummarySchema | undefined | null;
}) => {
  const durationData = useMemo(() => {
    if (!runSummary) return { display: "N/A", value: 0 };

    let minutes = 0;
    let seconds = 0;

    if (
      runSummary.total_duration_ms !== null &&
      runSummary.total_duration_ms !== undefined
    ) {
      const totalSeconds = Math.round(runSummary.total_duration_ms / 1000);
      minutes = Math.floor(totalSeconds / 60);
      seconds = totalSeconds % 60;
    } else if (runSummary.timestamp_start && runSummary.timestamp_end) {
      try {
        const start = new Date(runSummary.timestamp_start).getTime();
        const end = new Date(runSummary.timestamp_end).getTime();
        if (!isNaN(start) && !isNaN(end)) {
          const diffSeconds = Math.round((end - start) / 1000);
          if (diffSeconds >= 0) {
            minutes = Math.floor(diffSeconds / 60);
            seconds = diffSeconds % 60;
          }
        }
      } catch (e) {
        logger.error("Error parsing run timestamps for duration chart", {
          error: e,
        });
      }
    }

    const display =
      minutes > 0 || seconds > 0 ? `${minutes}m ${seconds}s` : "N/A";
    const value = minutes * 60 + seconds;

    return { display, value };
  }, [runSummary]);

  const chartData = [
    {
      label: "Duration",
      value: durationData.value,
      fill: "url(#runDurationChartGradient)",
    },
  ];

  return (
    <div className="card flex h-full flex-col bg-base-100 shadow">
      <div className="card-body p-4">
        <h3 className="card-title justify-center text-center text-lg">
          Run Duration
        </h3>
        <p className="mb-2 text-center text-sm text-base-content/70">
          Total analysis time for the latest run.
        </p>
        <div className="mx-auto aspect-square max-h-[200px] flex-grow">
          <ResponsiveContainer width="100%" height="100%">
            <RadialBarChart
              data={chartData}
              startAngle={90}
              endAngle={-270}
              innerRadius="70%"
              outerRadius="90%"
              barSize={10}
            >
              <defs>
                <linearGradient
                  id="runDurationChartGradient"
                  x1="0"
                  y1="0"
                  x2="1"
                  y2="0"
                >
                  <stop offset="0%" stopColor="hsl(var(--pc))" />
                  <stop offset="100%" stopColor="hsl(var(--sc))" />
                </linearGradient>
              </defs>
              <PolarGrid gridType="circle" radialLines={false} stroke="none" />
              <RadialBar
                dataKey="value"
                cornerRadius={5}
                background={{ fill: "hsl(var(--b2))" }}
              />
              <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
                <RechartsLabel
                  content={({ viewBox }) => {
                    if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                      return (
                        <text
                          x={viewBox.cx}
                          y={viewBox.cy}
                          textAnchor="middle"
                          dominantBaseline="middle"
                        >
                          <tspan
                            x={viewBox.cx}
                            y={viewBox.cy}
                            className="fill-base-content text-3xl font-bold"
                          >
                            {durationData.display}
                          </tspan>
                          <tspan
                            x={viewBox.cx}
                            y={(viewBox.cy || 0) + 20}
                            className="fill-base-content/70 text-xs"
                          >
                            {runSummary
                              ? new Date(
                                  runSummary.timestamp_start,
                                ).toLocaleDateString("sv-SE")
                              : ""}
                          </tspan>
                        </text>
                      );
                    }
                    return null;
                  }}
                />
              </PolarRadiusAxis>
            </RadialBarChart>
          </ResponsiveContainer>
        </div>
        {runSummary?.timestamp_end && (
          <div className="mt-2 text-center text-xs text-base-content/70">
            Completed:{" "}
            {new Date(runSummary.timestamp_end).toLocaleString("sv-SE")}
          </div>
        )}
      </div>
    </div>
  );
};

// --- NEW: Add missing schemas used by /canonical/release-notes response ---
interface ReleaseNoteSchema {
  // Corresponds to ReleaseNoteSchema in release_notes.py
  id: string; // aliased from _id
  title: string;
  version?: string | null; // Made version optional to match backend change
  url?: string | null;
  status: string;
  last_seen_run_id?: string | null;
  run_ids: string[];
  db_last_updated?: string | null; // Assuming datetime is stringified
  // Add other fields from backend schema if needed by other parts of the page
}

interface PaginatedReleaseNotesSchema {
  // Corresponds to PaginatedReleaseNotesSchema in release_notes.py
  total_notes: number;
  notes: ReleaseNoteSchema[];
  page: number;
  page_size: number;
  total_pages: number;
}
// --- END NEW ---

// --- NEW CHART COMPONENTS ---

interface StatusDistributionPieChartProps {
  data: Array<{ status: string; count: number }>;
  title: string;
  icon?: React.ElementType;
}

const StatusDistributionPieChart: React.FC<StatusDistributionPieChartProps> = ({
  data,
  title,
  icon: IconComponent,
}) => {
  const chartData: PieChartDataPoint[] = useMemo(() => {
    return data
      .map((item) => ({ name: item.status, value: item.count }))
      .sort((a, b) => b.value - a.value);
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-base-content/70">
        No {title.toLowerCase()} data available.
      </p>
    );
  }

  return (
    <div className="card flex h-full flex-col bg-base-100 shadow">
      <div className="card-body p-4">
        <h3 className="card-title flex items-center gap-2 text-lg">
          {IconComponent && (
            <IconComponent className="h-5 w-5 text-primary/80" />
          )}{" "}
          {title}
        </h3>
        <p className="mb-4 text-sm text-base-content/70">
          Distribution by status.
        </p>
        <div className="flex-grow" style={{ minHeight: "250px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) =>
                  `${name} (${(percent * 100).toFixed(0)}%)`
                }
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                stroke="hsl(var(--b1))"
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${entry.name}-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                wrapperClassName="tooltip-wrapper"
                contentStyle={{
                  backgroundColor: "hsl(var(--b1))",
                  borderRadius: "0.5rem",
                }}
              />
              <Legend wrapperStyle={{ fontSize: "12px" }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

interface AssignmentDistributionBarChartProps {
  data: Array<{
    assignee: {
      username: string;
      name?: string | null;
      avatar_url?: string | null;
    };
    count: number;
  }>;
  title: string;
  icon?: React.ElementType;
}

const AssignmentDistributionBarChart: React.FC<
  AssignmentDistributionBarChartProps
> = ({ data, title, icon: IconComponent }) => {
  const chartData = useMemo(() => {
    return data
      .map((item) => ({
        name: item.assignee.name || item.assignee.username,
        value: item.count,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10); // Show top 10 assignees
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-base-content/70">
        No {title.toLowerCase()} data available.
      </p>
    );
  }

  return (
    <div className="card flex h-full flex-col bg-base-100 shadow">
      <div className="card-body p-4">
        <h3 className="card-title flex items-center gap-2 text-lg">
          {IconComponent && (
            <IconComponent className="h-5 w-5 text-primary/80" />
          )}{" "}
          {title}
        </h3>
        <p className="mb-4 text-sm text-base-content/70">
          Distribution by assignee.
        </p>
        <div className="flex-grow" style={{ minHeight: "300px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis
                type="number"
                allowDecimals={false}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                dataKey="name"
                type="category"
                tick={{ fontSize: 10 }}
                width={150}
                interval={0}
              />
              <Tooltip
                wrapperClassName="tooltip-wrapper"
                contentStyle={{
                  backgroundColor: "hsl(var(--b1))",
                  borderRadius: "0.5rem",
                }}
              />
              <Legend wrapperStyle={{ fontSize: "12px" }} />
              <Bar
                dataKey="value"
                name="Count"
                fill="#82ca9d"
                radius={[0, 4, 4, 0]}
                barSize={15}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// --- END NEW CHART COMPONENTS ---

// --- Main Statistics Component ---
const Statistics: React.FC<StatisticsProps> = ({ apiBaseUrl }) => {
  const [statsData, setStatsData] = useState<StatisticsSchema | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { setBreadcrumbs } = useBreadcrumbs();
  const [notes, setNotes] = useState<ReleaseNoteSummarySchema[]>([]);
  const [notesError, setNotesError] = useState<string | null>(null);

  const [isTriggering, setIsTriggering] = useState(false);
  const [triggerMessage, setTriggerMessage] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState<boolean>(false);
  const [retryRunId, setRetryRunId] = useState<string>("");
  const [options, setOptions] = useState<PipelineTriggerOptions>({
    run_fetch: false,
    run_preprocess: false,
    run_analyze: false,
    run_prepare_queries: false,
    run_execute_queries: false,
    run_populate_db: false,
    run_generate_missing: false,
    run_all: false,
    purge_db: false,
    max_queries: null,
    model_id: null,
  });

  const triggerModalRef = useRef<HTMLDialogElement>(null);
  const retryModalRef = useRef<HTMLDialogElement>(null);

  const fetchStatistics = useCallback(async () => {
    try {
      const response = await axios.get<StatisticsSchema>(
        `${apiBaseUrl}/statistics`,
      );
      setStatsData(response.data);
      if (response.data?.run_history?.length > 0) {
        setRetryRunId(response.data.run_history[0].id);
      }
    } catch (err) {
      logger.error("Error fetching statistics", {
        error: err instanceof Error ? err.message : String(err),
        url: `${apiBaseUrl}/statistics`,
      });
      let errorMsg = "Failed to load statistics.";
      if (axios.isAxiosError(err) && err.response) {
        errorMsg = `API Error: ${err.response.data?.detail || err.message}`;
      } else if (err instanceof Error) {
        errorMsg = `Error: ${err.message}`;
      }
      setError((prev) => (prev ? `${prev} | ${errorMsg}` : errorMsg));
      setStatsData(null);
    }
  }, [apiBaseUrl]);

  const fetchNotesForCharts = useCallback(async () => {
    const params = new URLSearchParams({
      sort_by: "version",
      sort_desc: "true",
      page_size: "1000",
    });

    try {
      const response = await axios.get<PaginatedReleaseNotesSchema>(
        `${apiBaseUrl}/canonical/release-notes`,
        { params },
      );
      setNotes(response.data.notes as unknown as ReleaseNoteSummarySchema[]);
    } catch (err) {
      logger.error(
        "Error fetching release notes for charts from canonical endpoint",
        {
          error: err instanceof Error ? err.message : String(err),
          url: `${apiBaseUrl}/canonical/release-notes`,
          params: Object.fromEntries(params),
        },
      );
      let errorMsg = "Failed to load release notes for charts.";
      if (axios.isAxiosError(err) && err.response) {
        errorMsg = `Notes API Error: ${
          err.response.data?.detail || err.message
        }`;
      } else if (err instanceof Error) {
        errorMsg = `Notes Error: ${err.message}`;
      }
      setNotesError((prev) => (prev ? `${prev} | ${errorMsg}` : errorMsg));
    }
  }, [apiBaseUrl]);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotesError(null);
    await Promise.all([fetchStatistics(), fetchNotesForCharts()]);
    setLoading(false);
  }, [fetchStatistics, fetchNotesForCharts]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  useEffect(() => {
    setBreadcrumbs([{ label: "Statistics" }]);
  }, [setBreadcrumbs]);

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString("sv-SE", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      logger.warn("Error formatting date", { error, dateString });
      return "Invalid Date";
    }
  };

  const formatNumber = (num?: number | null) => {
    if (num === null || num === undefined) return "N/A";
    return num.toLocaleString();
  };

  const calculateTotalUpdatesNeeded = useMemo(() => {
    return (
      statsData?.updates_needed_by_section_title?.reduce(
        (sum, item) => sum + item.count,
        0,
      ) ??
      statsData?.updates_needed_by_category?.reduce(
        (sum, item) => sum + item.count,
        0,
      ) ??
      0
    );
  }, [statsData]);

  const totalArticlesAnalyzed = useMemo(() => {
    const relevanceCount =
      statsData?.relevance_score_distribution?.reduce(
        (sum, bucket) => sum + bucket.count,
        0,
      ) ?? 0;
    if (relevanceCount > 0) return relevanceCount;
    return (
      statsData?.similarity_score_distribution?.reduce(
        (sum, bucket) => sum + bucket.count,
        0,
      ) ?? 0
    );
  }, [statsData]);

  const handleCheckboxChange = (
    field: keyof PipelineTriggerOptions,
    checked: boolean | "indeterminate" | undefined,
  ) => {
    setOptions((prev) => ({ ...prev, [field]: !!checked }));
  };

  const handleMaxQueriesChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    setOptions((prev) => ({
      ...prev,
      max_queries: value ? parseInt(value, 10) : null,
    }));
  };

  const handleTrigger = async () => {
    setIsTriggering(true);
    setTriggerMessage(null);
    const result = await triggerPipeline(apiBaseUrl, options);
    if (result) {
      setTriggerMessage(
        `${result.message} Args: ${result.arguments.join(" ")}`,
      );
      triggerModalRef.current?.close();
      setTimeout(() => loadAllData(), 3000);
    } else {
      setTriggerMessage("Failed to start pipeline.");
    }
    setTimeout(() => setIsTriggering(false), 1000);
  };

  const booleanOptionKeys = Object.keys(options).filter(
    (k) => typeof options[k as keyof PipelineTriggerOptions] === "boolean",
  ) as (keyof PipelineTriggerOptions)[];

  const handleRetryRunIdChange = (value: string) => {
    setRetryRunId(value);
  };

  const handleRerunFailedQueries = async () => {
    if (!retryRunId) {
      setTriggerMessage("Please select a run to retry.");
      return;
    }
    setIsRetrying(true);
    setTriggerMessage(null);
    let runDescription = "the selected run";
    if (statsData?.run_history) {
      const selectedRun = statsData.run_history.find(
        (run) => run.id === retryRunId,
      );
      if (selectedRun?.timestamp_start) {
        runDescription = `run from ${new Date(selectedRun.timestamp_start).toLocaleDateString("sv-SE")}`;
      }
    }
    const result = await rerunFailedQueries(apiBaseUrl, retryRunId);
    if (result) {
      setTriggerMessage(
        `Successfully started retry of failed queries for ${runDescription}.`,
      );
      retryModalRef.current?.close();
      setTimeout(() => loadAllData(), 3000);
    } else {
      setTriggerMessage(
        `Failed to retry failed queries for ${runDescription}.`,
      );
    }
    setIsRetrying(false);
  };

  const formatRunOption = (run: RunSummarySchema) => {
    const date = run.timestamp_start
      ? new Date(run.timestamp_start).toLocaleDateString("sv-SE")
      : "Unknown date";
    const status = run.timestamp_end
      ? `Completed ${new Date(run.timestamp_end).toLocaleDateString("sv-SE")}`
      : "In progress";
    const successRate =
      run.total_queries_attempted > 0
        ? `(${((run.total_queries_succeeded / run.total_queries_attempted) * 100).toFixed(1)}% success)`
        : "";
    return `${date} - ${status} ${successRate}`;
  };

  const latestRun = statsData?.run_history?.[0];
  const successRate = statsData?.latest_run_success_rate;

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <h1 className="text-3xl font-bold tracking-tight">Statistics</h1>
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-16 w-16 animate-spin text-primary" />
          <p className="ml-4 text-lg text-base-content/80">
            Loading statistics data...
          </p>
        </div>
      </div>
    );
  }

  const combinedError = [error, notesError].filter(Boolean).join(" | ");
  if (combinedError) {
    return (
      <div className="p-6">
        <h1 className="mb-4 text-3xl font-bold tracking-tight">Statistics</h1>
        <div role="alert" className="alert alert-error shadow-lg">
          <AlertTriangle className="h-6 w-6" />
          <div>
            <h3 className="font-bold">Error Loading Statistics</h3>
            <div className="text-xs">{combinedError}</div>
          </div>
          <button className="btn btn-outline btn-sm" onClick={loadAllData}>
            Retry All
          </button>
        </div>
      </div>
    );
  }

  if (!statsData) {
    return (
      <div className="p-6">
        <h1 className="mb-4 text-3xl font-bold tracking-tight">Statistics</h1>
        <div className="card bg-base-100 text-center shadow-xl">
          <div className="card-body items-center p-12">
            <Info className="mx-auto mb-4 h-12 w-12 text-base-content/50" />
            <p className="text-base-content/80">No statistics available.</p>
            <button
              className="btn mt-4 btn-outline btn-sm"
              onClick={loadAllData}
            >
              Reload Data
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Render Dashboard Content ---
  return (
    <div className="min-h-screen space-y-8 bg-base-200/30 p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4 border-b border-base-300 pb-4">
        <h1 className="text-4xl font-bold tracking-tight text-base-content">
          Analysis Statistics
        </h1>
        <div className="flex items-center gap-3">
          <button
            className="btn btn-outline btn-sm btn-primary"
            onClick={() => triggerModalRef.current?.showModal()}
            disabled={isTriggering}
          >
            <Activity className="mr-1 h-4 w-4" /> Trigger Pipeline
          </button>
          <button
            className="btn btn-outline btn-sm btn-secondary"
            onClick={() => retryModalRef.current?.showModal()}
            disabled={isRetrying}
          >
            <ListChecks className="mr-1 h-4 w-4" /> Retry Failed Queries
          </button>
        </div>
      </div>

      {/* --- Key Metrics Row --- */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <div className="card bg-base-100 shadow">
          <div className="card-body p-5">
            <div className="flex items-center justify-between pb-2">
              <h3 className="card-title text-sm font-medium text-base-content/70 uppercase">
                Latest Analysis Run
              </h3>
              <Activity className="h-5 w-5 text-info" />
            </div>
            <div className="text-3xl font-bold text-base-content">
              {latestRun ? formatDate(latestRun.timestamp_end) : "N/A"}
            </div>
            <p className="pt-1 text-xs text-base-content/60">
              Started:{" "}
              {latestRun ? formatDate(latestRun.timestamp_start) : "N/A"}
            </p>
          </div>
        </div>

        <div className="card bg-base-100 shadow">
          <div className="card-body p-5">
            <div className="flex items-center justify-between pb-2">
              <h3 className="card-title text-sm font-medium text-base-content/70 uppercase">
                Run Success Rate
              </h3>
              <Gauge className="h-5 w-5 text-success" />
            </div>
            <div className="text-3xl font-bold text-base-content">
              {successRate && successRate.attempted > 0
                ? `${((successRate.succeeded / successRate.attempted) * 100).toFixed(1)}%`
                : "N/A"}
            </div>
            <p className="pt-1 text-xs text-base-content/60">
              {successRate
                ? `${formatNumber(successRate.succeeded)} succeeded / ${formatNumber(successRate.attempted)} attempted`
                : "No data"}
            </p>
          </div>
        </div>

        <div className="card bg-base-100 shadow">
          <div className="card-body p-5">
            <div className="flex items-center justify-between pb-2">
              <h3 className="card-title text-sm font-medium text-base-content/70 uppercase">
                Total Updates Needed
              </h3>
              <ListChecks className="h-5 w-5 text-warning" />
            </div>
            <div className="text-3xl font-bold text-base-content">
              {formatNumber(calculateTotalUpdatesNeeded)}
            </div>
            <p className="pt-1 text-xs text-base-content/60">
              Across {formatNumber(totalArticlesAnalyzed)} analyzed articles
            </p>
          </div>
        </div>

        <div className="card bg-base-100 shadow">
          <div className="card-body p-5">
            <div className="flex items-center justify-between pb-2">
              <h3 className="card-title text-sm font-medium text-base-content/70 uppercase">
                Missing Articles
              </h3>
              <FileWarning className="h-5 w-5 text-error" />
            </div>
            <div className="text-3xl font-bold text-base-content">
              {formatNumber(statsData.current_missing_articles_count)}
            </div>
            <Link
              to="/missing-articles"
              className="flex items-center pt-1 text-xs text-info hover:underline"
            >
              View List <ArrowRight className="ml-1 h-3 w-3" />
            </Link>
          </div>
        </div>
      </div>

      {/* --- New Charts Section --- */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ScoreDistributionChart
          data={statsData.relevance_score_distribution}
          title="Relevance Score Distribution"
          barColor="hsl(var(--a))"
        />
        <ScoreDistributionChart
          data={statsData.similarity_score_distribution}
          title="Similarity Score Distribution"
          barColor="hsl(var(--af))"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
        <UpdatesByCategoryChart data={statsData.updates_needed_by_category} />
        <UpdatesBySectionChart
          data={statsData.updates_needed_by_section_title}
        />
        <TokenUsageByNoteChart
          data={statsData.token_usage_by_note}
          notes={notes}
        />
      </div>

      {/* --- Article & Release Note Specific Stats --- */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <StatusDistributionPieChart
          data={statsData.article_status_distribution}
          title="Article Status Distribution"
          icon={PieChartIcon}
        />
        <AssignmentDistributionBarChart
          data={statsData.article_assignment_distribution}
          title="Article Assignment Distribution"
          icon={BarChart3}
        />
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <StatusDistributionPieChart
          data={statsData.release_note_status_distribution}
          title="Release Note Status Distribution"
          icon={PieChartIcon}
        />
        <AssignmentDistributionBarChart
          data={statsData.release_note_assignment_distribution}
          title="Release Note Assignment Distribution"
          icon={BarChart3}
        />
      </div>

      {/* --- Existing Charts and Run History (if any) --- */}
      {latestRun && (
        <div className="card mt-6 bg-base-100 shadow">
          <div className="card-body space-y-4 p-4">
            <h2 className="card-title text-xl">Latest Run Details</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              <RunDurationChart runSummary={latestRun} />
            </div>
          </div>
        </div>
      )}

      {statsData.run_history && statsData.run_history.length > 0 && (
        <div className="card mt-6 bg-base-100 shadow">
          <div className="card-body p-4">
            <h2 className="mb-3 card-title text-xl">Run History</h2>
            <div className="overflow-x-auto">
              <table className="table w-full table-sm">
                <thead>
                  <tr>
                    <th>Start Time</th>
                    <th>End Time</th>
                    <th>Duration</th>
                    <th>Attempted</th>
                    <th>Succeeded</th>
                    <th>Failed</th>
                    <th>Cost</th>
                    <th>Run ID</th>
                  </tr>
                </thead>
                <tbody>
                  {statsData.run_history.slice(0, 10).map((run) => (
                    <tr key={run.id} className="hover">
                      <td>{formatDate(run.timestamp_start)}</td>
                      <td>{formatDate(run.timestamp_end)}</td>
                      <td>
                        {run.total_duration_ms
                          ? `${Math.floor(run.total_duration_ms / 60000)}m ${Math.round((run.total_duration_ms % 60000) / 1000)}s`
                          : run.timestamp_start && run.timestamp_end
                            ? `${Math.floor((new Date(run.timestamp_end).getTime() - new Date(run.timestamp_start).getTime()) / 60000)}m ${Math.round(((new Date(run.timestamp_end).getTime() - new Date(run.timestamp_start).getTime()) % 60000) / 1000)}s`
                            : "N/A"}
                      </td>
                      <td>{formatNumber(run.total_queries_attempted)}</td>
                      <td className="text-success">
                        {formatNumber(run.total_queries_succeeded)}
                      </td>
                      <td className="text-error">
                        {formatNumber(run.total_queries_failed)}
                      </td>
                      <td>${run.total_actual_cost?.toFixed(2) ?? "N/A"}</td>
                      <td className="font-mono text-xs">{run.run_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* --- Modals (existing) --- */}
      <dialog
        id="trigger_pipeline_modal"
        ref={triggerModalRef}
        className="modal"
      >
        <div className="modal-box sm:max-w-md">
          <form method="dialog">
            <button className="btn absolute top-2 right-2 btn-circle btn-ghost btn-sm">
              ✕
            </button>
          </form>
          <h3 className="text-lg font-bold">Trigger Pipeline</h3>
          <p className="py-4 text-sm">
            Select steps and options for the pipeline run.
          </p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-4 py-4">
            {booleanOptionKeys.map((key) => (
              <div key={key} className="form-control">
                <label className="label cursor-pointer justify-start space-x-2">
                  <input
                    type="checkbox"
                    id={key}
                    checked={options[key] as boolean}
                    onChange={(e) =>
                      handleCheckboxChange(key, e.target.checked)
                    }
                    disabled={isTriggering}
                    className="checkbox checkbox-sm checkbox-primary"
                  />
                  <span className="label-text text-sm font-medium capitalize">
                    {key.replace(/^run_/, "").replace(/_/g, " ")}
                  </span>
                </label>
              </div>
            ))}
          </div>
          <div className="divider my-4"></div>
          <div className="space-y-2">
            <label htmlFor="max_queries" className="label text-sm font-medium">
              <span className="label-text">Max AI Queries (Optional)</span>
            </label>
            <input
              id="max_queries"
              type="number"
              placeholder="e.g., 100"
              value={options.max_queries ?? ""}
              onChange={handleMaxQueriesChange}
              disabled={isTriggering}
              className="input-bordered input w-full"
              min="1"
            />
            <p className="mt-1 text-xs text-base-content/70">
              Limits queries for 'Execute AI Queries' step.
            </p>
          </div>
          {triggerMessage && (
            <p
              className={`mt-4 text-sm ${triggerMessage.startsWith("Failed") ? "text-error" : "text-success"}`}
            >
              {triggerMessage}
            </p>
          )}
          <div className="modal-action mt-6">
            <form method="dialog">
              <button className="btn btn-ghost">Cancel</button>
            </form>
            <button
              className="btn btn-primary"
              onClick={handleTrigger}
              disabled={isTriggering}
            >
              {isTriggering ? (
                <span className="loading loading-xs loading-spinner"></span>
              ) : (
                "Run Selected Steps"
              )}
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button>close</button>
        </form>
      </dialog>

      <dialog
        id="retry_failed_queries_modal"
        ref={retryModalRef}
        className="modal"
      >
        <div className="modal-box sm:max-w-md">
          <form method="dialog">
            <button className="btn absolute top-2 right-2 btn-circle btn-ghost btn-sm">
              ✕
            </button>
          </form>
          <h3 className="text-lg font-bold">Retry Failed Queries</h3>
          <p className="py-4 text-sm">
            Select a previous run to retry its failed queries.
          </p>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label
                htmlFor="run_id_select"
                className="label text-sm font-medium"
              >
                <span className="label-text">Select Run</span>
              </label>
              {statsData?.run_history && statsData.run_history.length > 0 ? (
                <>
                  <select
                    id="run_id_select"
                    className="select-bordered select w-full"
                    value={retryRunId}
                    onChange={(e) => handleRetryRunIdChange(e.target.value)}
                    disabled={isRetrying}
                  >
                    <option value="">Select a run...</option>
                    {statsData.run_history.map((run) => (
                      <option key={run.id} value={run.id}>
                        {formatRunOption(run)}
                      </option>
                    ))}
                  </select>
                  {retryRunId &&
                    statsData?.run_history?.find((run) => run.id === retryRunId)
                      ?.total_queries_failed === 0 && (
                      <p className="mt-1 text-xs text-success">
                        This run has no failed queries to retry.
                      </p>
                    )}
                  {retryRunId &&
                    (statsData?.run_history?.find(
                      (run) => run.id === retryRunId,
                    )?.total_queries_failed ?? 0) > 0 && (
                      <p className="mt-1 text-xs text-warning">
                        This run has{" "}
                        {
                          statsData?.run_history?.find(
                            (run) => run.id === retryRunId,
                          )?.total_queries_failed
                        }{" "}
                        failed queries.
                      </p>
                    )}
                </>
              ) : (
                <p className="text-sm text-base-content/70">
                  No run history available.
                </p>
              )}
            </div>
            {triggerMessage && (
              <p
                className={`text-sm ${triggerMessage.startsWith("Failed") ? "text-error" : "text-success"}`}
              >
                {triggerMessage}
              </p>
            )}
          </div>
          <div className="modal-action">
            <form method="dialog">
              <button className="btn btn-ghost">Cancel</button>
            </form>
            <button
              className="btn btn-primary"
              onClick={handleRerunFailedQueries}
              disabled={
                isRetrying ||
                !retryRunId ||
                statsData?.run_history?.find((run) => run.id === retryRunId)
                  ?.total_queries_failed === 0
              }
            >
              {isRetrying ? (
                <span className="loading loading-xs loading-spinner"></span>
              ) : (
                "Retry Queries"
              )}
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button>close</button>
        </form>
      </dialog>
    </div>
  );
};

export default Statistics;
